import React, { useState } from 'react';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';
import VoiceSelect from './VoiceSelect';

interface QuickCreateDoubleModalProps {
  onClose: () => void;
  onSubmit: (formData: {
    name: string;
    purpose: string;
    voice_id: string;
    voice_name: string | null;
    knowledgeBaseType: 'create' | 'assist';
    first_message: string;
    prompt: string;
    knowledge_base: string;
  }) => Promise<void>;
}

const QuickCreateDoubleModal = ({ onClose, onSubmit }: QuickCreateDoubleModalProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    purpose: '',
    first_message: '',
    prompt: '',
    knowledge_base: '',
    voice_id: '',
    voice_name: null as string | null,
    knowledgeBaseType: 'create' as const
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error("Error creating double:", error);
      // Display error message
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    setCurrentPage(2);
  };

  const handleBack = () => {
    setCurrentPage(1);
  };
  
  const isFirstPageValid = formData.name && formData.voice_id && formData.knowledgeBaseType && formData.purpose;
  const isSecondPageValid = formData.prompt && formData.knowledge_base;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl w-full max-w-2xl flex flex-col max-h-[calc(100vh-32px)]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-4xl font-bold text-gray-900">Create New Double</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="sticky top-[92px] bg-white px-6 pt-6 z-10">
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary transition-all duration-300 ease-out"
              style={{ width: `${(currentPage / 2) * 100}%` }}
            />
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto flex-1">
          {currentPage === 1 ? (
            <>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  What do you want to name this double?
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="Give your double a name"
                />
              </div>
              
              <div>
                <label htmlFor="purpose" className="block text-sm font-medium text-gray-700 mb-2">
                  What do you want to use this double for?
                </label>
                <input
                  type="text"
                  id="purpose"
                  required
                  value={formData.purpose}
                  onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  placeholder="Describe the main purpose of this double"
                />
              </div>

              <VoiceSelect
                value={formData.voice_id}
                onChange={(voiceId) => setFormData(prev => ({ ...prev, voice_id: voiceId }))}
                onSelect={(voice) => setFormData(prev => ({ ...prev, voice_name: voice.name }))}
                label="What voice should your double use?"
                helpText="Choose the ElevenLabs voice to use for this double."
              />

              <div>
                <label htmlFor="knowledgeBaseType" className="block text-sm font-medium text-gray-700 mb-2">
                  Do you want to create a knowledge base for your double or have us help you?
                </label>
                <select
                  id="knowledgeBaseType"
                  required
                  value={formData.knowledgeBaseType}
                  onChange={(e) => setFormData(prev => ({ ...prev, knowledgeBaseType: e.target.value as 'create' | 'assist' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="create">I want to create it myself</option>
                  <option value="assist">I want help creating it (onboarding fee applies)</option>
                </select>
              </div>
            </>
          ) : (
            <>
              <div>
                <label htmlFor="firstMessage" className="block text-sm font-medium text-gray-700 mb-2">
                  First Message
                </label>
                <textarea
                  id="firstMessage"
                  value={formData.first_message}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_message: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-24"
                  placeholder="Enter the first message your double will say when starting a conversation..."
                />
                <p className="mt-1 text-sm text-gray-500">
                  This message will be used to start every conversation with your double.
                </p>
              </div>

              <div>
                <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
                  System Prompt
                  <span className="ml-1 text-gray-500">(System Instructions)</span>
                </label>
                <textarea
                  id="prompt"
                  required
                  value={formData.prompt}
                  onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-40"
                  placeholder="Enter the system instructions for your double..."
                />
              </div>

              <div>
                <label htmlFor="knowledgeBase" className="block text-sm font-medium text-gray-700 mb-2">
                  Knowledge Base
                </label>
                <textarea
                  id="knowledgeBase"
                  required
                  value={formData.knowledge_base}
                  onChange={(e) => setFormData(prev => ({ ...prev, knowledge_base: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-40"
                  placeholder="Enter the knowledge base for your double..."
                />
              </div>
            </>
          )}
          
          <div className="flex justify-between items-center gap-3 pt-4 mt-8">
            {currentPage === 1 ? (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!isFirstPageValid}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  Next
                  <ArrowRight className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={!isSecondPageValid || isSubmitting}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-1"></span>
                      Creating...
                    </>
                  ) : (
                    <>
                      Create Double
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickCreateDoubleModal;
